import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileXls, FilePdf, MagnifyingGlass, X, ClipboardText } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";

const EMPTY_FILTERS = {
  no_so: "",
  distrik_id: "__any__",
  date_from: "",
  date_to: "",
};

const STATUS_LABEL = {
  belum_termuat: "Belum Termuat",
  sudah_termuat: "Sudah Termuat",
  termuat: "Termuat",
  diverifikasi: "Diverifikasi",
  mismatch: "Selisih",
  dibatalkan: "Dibatalkan",
};

export default function SOUsageReport() {
  const [districts, setDistricts] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ row_count: 0, total_qty: 0, unique_so_count: 0, unique_truck_count: 0, truncated: false, max_rows: 0 });
  const [loading, setLoading] = useState(false);
  // Monotonic token so a fast series of Apply clicks can't render a stale response.
  const loadToken = React.useRef(0);

  const load = useCallback(async (nextFilters) => {
    const my = ++loadToken.current;
    setLoading(true);
    try {
      const f = nextFilters || filters;
      const params = {};
      if (f.no_so.trim()) params.no_so = f.no_so.trim();
      if (f.distrik_id && f.distrik_id !== "__any__") params.distrik_id = f.distrik_id;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const { data } = await api.get("/reports/so-usage", { params });
      if (my !== loadToken.current) return; // superseded — drop
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch {
      if (my === loadToken.current) setRows([]);
    } finally {
      if (my === loadToken.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/districts", { params: { aktif: true } })
      .then((r) => setDistricts(r.data))
      .catch(() => setDistricts([]));
    load(EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => load(filters);
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    load(EMPTY_FILTERS);
  };

  const buildQS = () => {
    const p = new URLSearchParams();
    if (filters.no_so.trim()) p.set("no_so", filters.no_so.trim());
    if (filters.distrik_id && filters.distrik_id !== "__any__") p.set("distrik_id", filters.distrik_id);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    return p.toString();
  };

  const downloadExport = async (format) => {
    const qs = buildQS();
    const url = `${API_BASE}/reports/so-usage/export.${format}${qs ? `?${qs}` : ""}`;
    try {
      // Fetch as blob so httpOnly cookies still authenticate.
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pemakaian_so.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Export ${format.toUpperCase()} berhasil`);
    } catch (e) {
      toast.error(`Export gagal: ${e.message}`);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8 space-y-6" data-testid="so-usage-report-page">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <ClipboardText size={28} weight="duotone" className="text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Laporan Pemakaian SO</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Menampilkan truk-truk mana saja yang memakai tiap SO. Cocok untuk audit &amp; laporan bulanan.
            </p>
          </div>
        </div>
      </div>

      {/* Filter card */}
      <div className="border border-border bg-secondary/20 p-4 space-y-3" data-testid="filters-card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="overline block mb-1">No SO</Label>
            <Input
              placeholder="Contoh: SO-2024-001"
              value={filters.no_so}
              onChange={(e) => setFilters({ ...filters, no_so: e.target.value })}
              className="rounded-none h-10 font-mono"
              data-testid="filter-no-so"
            />
          </div>
          <div>
            <Label className="overline block mb-1">Distrik</Label>
            <Select
              value={filters.distrik_id}
              onValueChange={(v) => setFilters({ ...filters, distrik_id: v })}
            >
              <SelectTrigger className="rounded-none h-10" data-testid="filter-distrik">
                <SelectValue placeholder="Semua distrik" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Semua Distrik</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline block mb-1">Tanggal Dari</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="rounded-none h-10"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <Label className="overline block mb-1">Tanggal Sampai</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="rounded-none h-10"
              data-testid="filter-date-to"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={applyFilters} className="rounded-none" data-testid="apply-filters-btn">
            <MagnifyingGlass size={16} className="mr-2" /> Terapkan
          </Button>
          <Button variant="outline" onClick={resetFilters} className="rounded-none" data-testid="reset-filters-btn">
            <X size={16} className="mr-2" /> Reset
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => downloadExport("xlsx")} className="rounded-none" data-testid="export-xlsx-btn">
            <FileXls size={16} className="mr-2" /> Export Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExport("pdf")} className="rounded-none" data-testid="export-pdf-btn">
            <FilePdf size={16} className="mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-chips">
        <SummaryChip label="Total Baris" value={summary.row_count} />
        <SummaryChip label="Total Kuantitas" value={summary.total_qty} />
        <SummaryChip label="SO Unik" value={summary.unique_so_count} />
        <SummaryChip label="Truk Unik" value={summary.unique_truck_count} />
      </div>

      {summary.truncated && (
        <div className="p-3 bg-amber-50 border-l-4 border-amber-500 text-sm text-amber-900" data-testid="truncated-warn">
          ⚠ Data terlalu banyak — hanya <strong>{summary.max_rows}</strong> SPB pertama yang ditampilkan. Persempit filter (tanggal/distrik/no SO) untuk melihat sisanya.
        </div>
      )}

      {/* Table */}
      <div className="border border-border overflow-x-auto" data-testid="usage-table-wrapper">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 overline">No SO</th>
              <th className="px-3 py-2 overline">No SPB</th>
              <th className="px-3 py-2 overline">Tanggal</th>
              <th className="px-3 py-2 overline">No Polisi</th>
              <th className="px-3 py-2 overline">Sopir</th>
              <th className="px-3 py-2 overline">Ekspedisi</th>
              <th className="px-3 py-2 overline text-right">Kuantitas Termuat</th>
              <th className="px-3 py-2 overline">Status</th>
              <th className="px-3 py-2 overline">Distrik</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Memuat…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Tidak ada data untuk filter ini.</td></tr>
            )}
            {rows.map((r, idx) => (
              <tr
                key={`${r.id}-${r.no_so_used}-${idx}`}
                className="border-b border-border/40 hover:bg-secondary/30"
                data-testid={`usage-row-${idx}`}
              >
                <td className="px-3 py-2 font-mono font-bold">
                  <div className="flex items-center gap-2">
                    {r.no_so_used || "-"}
                    {r.over_quota && (
                      <span className="text-[9px] font-bold bg-destructive text-destructive-foreground px-1 py-0.5 uppercase tracking-wider">
                        Over
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono">{r.no_spb || "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatIdDate(r.tanggal) || "-"}</td>
                <td className="px-3 py-2 font-mono">{r.no_polisi || "-"}</td>
                <td className="px-3 py-2">{r.nama_supir || "-"}</td>
                <td className="px-3 py-2">{r.transporter || "-"}</td>
                <td className="px-3 py-2 font-mono text-right">
                  {r.jumlah_alokasi != null ? r.jumlah_alokasi : "-"}
                </td>
                <td className="px-3 py-2 text-xs uppercase tracking-wider">
                  {STATUS_LABEL[r.status] || r.status || "-"}
                </td>
                <td className="px-3 py-2">{r.distrik_nama || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryChip({ label, value }) {
  return (
    <div className="border border-border bg-background p-3">
      <div className="overline text-muted-foreground text-[10px]">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{value ?? 0}</div>
    </div>
  );
}
